import { resolveNavLinks } from "../../lib/navUtils";
import { MessageCircle, Mail, Clock, ArrowUp } from "lucide-react";
import { PLACEHOLDER_INSTAGRAM, PLACEHOLDER_INSTAGRAM_HANDLE, placeholder } from "../../lib/constants";

export default function Footer({ globals, whatsappNumber, navigation, uiStrings, enabledSections, hasServices = true, hasPortfolio = true, hasTestimonials = true, hasBlog = true, partnerLogos = [] }) {
  const year = new Date().getFullYear();
  const whatsapp = whatsappNumber || globals?.whatsapp_number || "";
  const waDigits = whatsapp.replace(/\D/g, "");
  
  const logoText = uiStrings?.general?.logo || globals?.logo || "";
  const logoSubtitle = uiStrings?.general?.subtitle || globals?.subtitle || "";
  const footerDesc = uiStrings?.general?.footer_description || globals?.footer_description || "";
  const contactEmail = globals?.contact_email || "";
  const locations = uiStrings?.general?.locations || globals?.locations || "";
  const copyright = uiStrings?.general?.copyright || globals?.copyright || placeholder("medium");
  const instagramUrl = globals?.instagram_url || PLACEHOLDER_INSTAGRAM;
  const instagramHandle = (() => {
    if (!globals?.instagram_url) return PLACEHOLDER_INSTAGRAM_HANDLE;
    try { const m = new URL(globals.instagram_url).pathname.replace(/\/$/, "").split("/").pop(); return m ? `@${m}` : PLACEHOLDER_INSTAGRAM_HANDLE; } catch { return PLACEHOLDER_INSTAGRAM_HANDLE; }
  })();

  const navHeading = uiStrings?.footer?.nav_heading || "";
  const contactHeading = uiStrings?.footer?.contact_heading || "";
  const navLinks = resolveNavLinks(navigation, enabledSections, { services: hasServices, portfolio: hasPortfolio, testimonials: hasTestimonials, blog: hasBlog });

  return (
    <footer data-id="site-footer" className="bg-softblack text-fendi/80 pt-20 pb-10 grain relative">
      <div data-id="footer-inner" className="max-w-7xl mx-auto px-6 md:px-12">
        <div data-id="footer-grid" className="grid md:grid-cols-2 lg:grid-cols-12 gap-10 pb-16 border-b border-fendi/20">
          <div data-id="footer-logo-column" className="lg:col-span-5">
            <p data-id="footer-logo-text" className="font-serif text-3xl text-offwhite">{logoText}<span data-id="footer-logo-dot" className="text-champagne">.</span>
              {logoSubtitle && <span data-id="footer-logo-subtitle" className="ml-4 text-[10px] tracking-[0.34em] uppercase text-fendi/60 align-middle">{logoSubtitle}</span>}
            </p>
            <p data-id="footer-description" className="mt-6 max-w-md font-light leading-relaxed text-sm text-fendi/60" dangerouslySetInnerHTML={{ __html: footerDesc }} />
          </div>
          <div data-id="footer-nav-column" className="lg:col-span-3">
            <p data-id="footer-nav-heading" className="eyebrow !text-champagne/80">{navHeading}</p>
            <ul data-id="footer-nav-list" className="mt-5 space-y-3 text-sm">
              {navLinks.map((link, i) => (
                <li key={i} data-id={`footer-nav-item-${link.label.toLowerCase()}`}>
                  <a data-id={`footer-nav-link-${link.label.toLowerCase()}`} className="inline-flex items-center gap-2 text-fendi/70 hover:text-offwhite transition-colors" href={link.href}>
                    <span data-id={`footer-nav-decoration-${link.label.toLowerCase()}`} className="w-0.5 h-0.5 rounded-full bg-champagne/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div data-id="footer-contact-column" className="lg:col-span-4">
            <p data-id="footer-contact-heading" className="eyebrow !text-champagne/80">{contactHeading}</p>
            <ul data-id="footer-contact-list" className="mt-5 space-y-3 text-sm">
              {whatsapp && (
                <li data-id="footer-contact-item-whatsapp">
                  <a data-id="footer-contact-link-whatsapp" className="inline-flex items-center gap-3 text-fendi/70 hover:text-offwhite transition-colors" href={`https://wa.me/${waDigits}`} target="_blank" rel="noopener noreferrer">
                    <MessageCircle data-id="footer-contact-icon-whatsapp" size={15} className="text-champagne/70 shrink-0" />
                    {whatsapp}
                  </a>
                </li>
              )}
              {contactEmail && (
                <li data-id="footer-contact-item-email">
                  <a data-id="footer-contact-link-email" className="inline-flex items-center gap-3 text-fendi/70 hover:text-offwhite transition-colors" href={`mailto:${contactEmail}`}>
                    <Mail data-id="footer-contact-icon-email" size={15} className="text-champagne/70 shrink-0" />
                    {contactEmail}
                  </a>
                </li>
              )}
              {locations && (
                <li data-id="footer-contact-item-location">
                  <span data-id="footer-contact-location" className="inline-flex items-center gap-3 text-fendi/70">
                    <Clock data-id="footer-contact-icon-location" size={15} className="text-champagne/70 shrink-0" />
                    {locations}
                  </span>
                </li>
              )}
              <li data-id="footer-contact-item-instagram">
                <a data-id="footer-contact-link-instagram" href={instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-champagne hover:text-champagne/80 transition-colors">
                  <svg data-id="footer-contact-icon-instagram" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-champagne/70 shrink-0">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                  <span data-id="footer-contact-handle-instagram" className="text-sm">{instagramHandle}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div data-id="footer-bottom-bar" className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-fendi/60">
          <p data-id="footer-copyright" className="text-justify" dangerouslySetInnerHTML={{ __html: `&copy; ${year} ${copyright}` }} />
          <button data-id="footer-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-1.5 hover:text-offwhite transition-colors">
            <ArrowUp data-id="footer-scroll-top-icon" size={12} />
            {uiStrings?.general?.scroll || placeholder("short")}
          </button>
        </div>
      </div>

      {partnerLogos.filter(Boolean).length > 0 && (
        <div data-id="footer-partners" className="mt-16 pt-10 border-t border-fendi/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-center text-[10px] tracking-[0.3em] uppercase text-fendi/50 mb-8">
              {uiStrings?.general?.about_partners || "ASSOCIAÇÕES"}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
              {partnerLogos.filter(Boolean).map((url, i) => (
                <div key={i} data-id={`footer-partner-logo-${i}`} className="h-10 md:h-14 flex items-center justify-center opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-500">
                  <img src={url} alt={`Parceiro ${i + 1}`} className="max-h-full w-auto object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
